import React, { FC, useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router";
import { Layout, View, useDevice } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { ProductPreview } from "../../../../../../domain/projections/ProductPreview";
import { useGetLookAndLikeProducts } from "../../../../behaviors/useGetLookAndLikeProducts";
import { ProductCarousel } from "../../../../components/productCarousel/ProductCarousel";
import { ProductGallery } from "../../../../components/productGallery/ProductGallery";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Routes } from "../../../../routing/routes";
import { List } from "../../../../tracking/tracking";
import { useTrackItemSelected } from "../../../../tracking/useTrackItemSelected";
import { style as lookAndLikeProductsStyle } from "./LookAndLikeProducts.styles";

interface LookAndLikeProductsProps {
  readonly fullWidth?: boolean;
  readonly onPressProduct?: (product: ProductPreview) => void;
}

const LookAndLikeProducts: FC<LookAndLikeProductsProps> = ({ fullWidth = true, onPressProduct }) => {
  const { products: lookAndLikeProducts } = useGetLookAndLikeProducts();
  const { screen } = useDevice();
  const {
    session: { user },
    basePath,
    tradename,
  } = useStaticInfo();
  const navigate = useNavigate();

  const crossSellingLookLikeTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CROSS_SELLING_LOOK_LIKE_TITLE,
  });

  const style = useMemo(() => lookAndLikeProductsStyle(), []);

  const trackItemSelected = useTrackItemSelected({
    country: user.country,
    segment: user.segment,
    userId: user.id,
    tradename,
  });

  const onPress = useCallback(
    (product: ProductPreview) => {
      trackItemSelected({
        list: List.LOOK_AND_LIKE,
        productPreview: product,
      });

      onPressProduct?.(product);

      navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId: product.id }), {
        state: { innerNavigation: true },
      });
    },
    [basePath, navigate, onPressProduct, trackItemSelected],
  );

  if (!lookAndLikeProducts.length) {
    return null;
  }

  return screen.L ? (
    fullWidth ? (
      <ProductGallery products={lookAndLikeProducts} title={crossSellingLookLikeTitleText} onPressProduct={onPress} />
    ) : (
      <Layout style={style.layoutContainer} fullWidth>
        <View style={style.wrapper}>
          <ProductGallery
            products={lookAndLikeProducts}
            title={crossSellingLookLikeTitleText}
            onPressProduct={onPress}
          />
        </View>
      </Layout>
    )
  ) : (
    <ProductCarousel products={lookAndLikeProducts} title={crossSellingLookLikeTitleText} onPressProduct={onPress} />
  );
};

export { LookAndLikeProducts };
